import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import type {
    FindingDocType,
    FindingPlanumDocType,
    FindingProfileDocType
} from '@ast-data/shared';
import {
    useSiblingLinks,
    type RecordSiblings
} from '../../components/useRecordSiblings';
import type { DialogBackState } from '../../components/dialogBackTrail';
import { useFamilyDocs, useProjectDocs } from '../../db/useScopedDocs';
import { useFamilyProjectIds } from '../projects/useFamilyProjectIds';
import { compareNatural } from '../../utils/compare';
import type { DescriptionSlot } from './descriptionValidation';
import { useFindingBasePath } from './findingRefLabels';

/**
 * This Befund's descriptions of one kind, in the order the pager steps them:
 * ascending by number, Teile in order under the number they divide — the same
 * order the Befunde tree lists its children in.
 */
export function orderedDescriptions<T extends DescriptionSlot>(
    rows: readonly T[],
    findingId: string
): T[] {
    return rows
        .filter((row) => row.findingId === findingId)
        .sort(
            (left, right) =>
                compareNatural(left.number, right.number) ||
                compareNatural(left.partLabel, right.partLabel)
        );
}

/** One stop of the Befund pager: a Befund, or one of its descriptions. */
export interface FindingSequenceEntry {
    kind: 'finding' | 'planum' | 'profile';
    id: string;
    /** The Befund itself, or the one the description belongs to. */
    findingId: string;
    findingNumber: string;
    /** The project the record lives in — its editor's route. */
    projectId: string;
    /** Planum-/Profilnummer; empty for a Befund. */
    number: string;
    partLabel: string;
}

type SequenceFinding = Pick<
    FindingDocType,
    'id' | 'findingNumber' | 'projectId'
>;
type SequenceDescription = DescriptionSlot & { projectId: string };

/**
 * Every stop the arrows of the Befund, Planum and Profil editors step through:
 * each Befund by Befundnummer, followed by its Plana, then its Profile —
 * Befund 5, Planum 1, Planum 2, Profil 5, Profil 6, Befund 6, …
 *
 * `planums` and `profiles` come as slots (`number` = Planum-/Profilnummer).
 */
export function findingSequence(
    findings: readonly SequenceFinding[],
    planums: readonly SequenceDescription[],
    profiles: readonly SequenceDescription[]
): FindingSequenceEntry[] {
    const groupByFinding = (rows: readonly SequenceDescription[]) => {
        const groups = new Map<string, SequenceDescription[]>();
        for (const row of rows) {
            const group = groups.get(row.findingId) ?? [];
            group.push(row);
            groups.set(row.findingId, group);
        }
        return groups;
    };
    const planumsOf = groupByFinding(planums);
    const profilesOf = groupByFinding(profiles);
    const ordered = [...findings].sort(
        (leftFinding, rightFinding) =>
            compareNatural(
                leftFinding.findingNumber,
                rightFinding.findingNumber
            ) || compareNatural(leftFinding.id, rightFinding.id)
    );
    return ordered.flatMap((finding): FindingSequenceEntry[] => {
        const describe =
            (kind: 'planum' | 'profile') =>
            (row: SequenceDescription): FindingSequenceEntry => ({
                kind,
                id: row.id,
                findingId: finding.id,
                findingNumber: finding.findingNumber,
                projectId: row.projectId,
                number: row.number,
                partLabel: row.partLabel
            });
        return [
            {
                kind: 'finding',
                id: finding.id,
                findingId: finding.id,
                findingNumber: finding.findingNumber,
                projectId: finding.projectId,
                number: '',
                partLabel: ''
            },
            ...orderedDescriptions(
                planumsOf.get(finding.id) ?? [],
                finding.id
            ).map(describe('planum')),
            ...orderedDescriptions(
                profilesOf.get(finding.id) ?? [],
                finding.id
            ).map(describe('profile'))
        ];
    });
}

/**
 * The arrows of the Befund, Planum and Profil editors: one run through the
 * project's Befunde with each one's Plana and Profile in between (see
 * {@link findingSequence}). The Befunde are the project's, as its tree lists
 * them; their descriptions the family's, since a shared Befund's Plana may sit
 * in any phase.
 *
 * A neighbour of the same Befund is named by itself ("Planum 2"), one across the
 * border with its Befund ("Bef. 5, Profil 6").
 */
export function useFindingSequence({
    projectId,
    currentId,
    requestNavigate
}: {
    projectId: string;
    /** The open record, or '' in a `new` dialog (which has no neighbours). */
    currentId: string;
    /** `guardedNav.requestNavigate` — leaving a dirty form has to ask first. */
    requestNavigate: (to: string, state?: DialogBackState) => void;
}): RecordSiblings {
    const intl = useIntl();
    const findingBase = useFindingBasePath();
    const familyIds = useFamilyProjectIds(projectId);
    const { results: findings } = useProjectDocs<FindingDocType>(
        'findings',
        projectId
    );
    const { results: planums } = useFamilyDocs<FindingPlanumDocType>(
        'finding_planums',
        familyIds
    );
    const { results: profiles } = useFamilyDocs<FindingProfileDocType>(
        'finding_profiles',
        familyIds
    );
    const ordered = useMemo(
        () =>
            findingSequence(
                findings,
                planums.map((planum) => ({
                    ...planum,
                    number: planum.planumNumber
                })),
                profiles.map((profile) => ({
                    ...profile,
                    number: profile.profileNumber
                }))
            ),
        [findings, planums, profiles]
    );
    const currentFindingId =
        ordered.find((entry) => entry.id === currentId)?.findingId ?? '';

    const ownLabel = (entry: FindingSequenceEntry) => {
        const part = entry.partLabel.trim();
        return intl.formatMessage(
            { id: `${entry.kind}.${part ? 'labelPart' : 'label'}` },
            { number: entry.number || '?', part }
        );
    };
    return useSiblingLinks({
        ordered,
        currentId,
        label: (entry) => {
            if (entry.kind === 'finding') {
                return intl.formatMessage(
                    { id: 'finding.crumb' },
                    { number: entry.findingNumber || '?' }
                );
            }
            if (entry.findingId === currentFindingId) {
                return ownLabel(entry);
            }
            return intl.formatMessage(
                { id: 'finding.sequenceOtherFinding' },
                {
                    finding: entry.findingNumber || '?',
                    label: ownLabel(entry)
                }
            );
        },
        to: (entry) => {
            const base = findingBase(entry.projectId, entry.findingId);
            if (entry.kind === 'finding') {
                return `${base}/edit`;
            }
            const segment = entry.kind === 'planum' ? 'planums' : 'profiles';
            return `${base}/${segment}/${entry.id}/edit`;
        },
        requestNavigate
    });
}
